/**
 * Naming and addressing a flight's file from its ROW, not from its storage key
 * (#39 Phase C).
 *
 * `fleet.flights.s3_key` is where the bytes are and nothing more. Since Phase C
 * it is an opaque uuid for anything newly uploaded, so the two things the UI used
 * to slice out of it — a display name, and an address to hand Data Analysis Pro —
 * have to come from the row instead: `log_basename` for the name, `object_id` for
 * the address.
 *
 * **These live here, extracted and pure, because the alternative is untestable.**
 * This app has no jsdom or RTL, so a component test is not available; and `tsc`
 * cannot help either, since `log_basename` and the key's last segment are both
 * `string` — swapping the precedence, or dropping the fallback, type-checks
 * perfectly. Pure functions are the only shape of this logic a test can reach,
 * which is the repo's standing convention for exactly this reason.
 *
 * The key-derived arm is kept in both, and it is the **documented legacy
 * exemption** rather than dead code: `log_basename` was backfilled only for the
 * rows the old index covered, so ~183 existing flights carry NULL, and
 * `object_id` is nullable by design. Coexistence is permanent — expect both arms
 * to stay live indefinitely.
 */

import { isPlottableFile } from '../components/file-browser/utils';

/** The subset of a flight row these helpers read. */
export interface FlightFileFields {
  /** Where the bytes are. Opaque since #39 Phase C — never parse it for meaning. */
  s3_key?: string | null;
  /** The log's original filename, written at ingest (#39 Phase B2). */
  log_basename?: string | null;
  /** `fleet.file_objects` row id, if the flight is linked to one. */
  object_id?: string | null;
  /** `'log'` (an uploaded logger CSV) or `'dlb_track'` (a DroneLogbook track, #1613). */
  source?: string | null;
}

/**
 * Whether a flight row came from an uploaded log, i.e. has a file and extracted data.
 *
 * A `'dlb_track'` row (#1613) holds flight hours only: no file, no metrics. A surface that
 * offers a flight's file or its metrics leaves it out; the flights list keeps it. A row read
 * before the column existed carries no `source` and is a log.
 */
export function isLogBacked(flight: FlightFileFields): boolean {
  return flight.source !== 'dlb_track';
}

/** What a `#s3Keys=` handoff entry may carry. Either address, never neither. */
export type FlightHandoffEntry =
  | { object_id: string; filename: string }
  | { s3_key: string; filename: string };

/**
 * The name to show for a flight's file.
 *
 * Row first. Falls back to the key's last segment for legacy rows, and to the
 * placeholder when there is nothing at all to show — a flight with neither is a
 * real state (`s3_key` is nullable), and rendering "undefined" is worse than
 * rendering nothing.
 *
 * **A key with no last segment yields the placeholder, not the key.** A
 * directory-style key (`organizations/copperbelt/2026/`) pops to `''`, and the
 * fallback used to be the whole key — so the dialog would print an internal S3
 * path in a field labelled "File". `flightDetails.utils.ts::flightFilename`,
 * which this replaced, returned `null` for exactly that shape and the dialog
 * hid the field; deleting it took that behaviour's only pin with it (#1294
 * review, F4). Restored here rather than at the call site, because all three
 * callers are display surfaces — the dialog, Latest Flights and the health
 * file-selector — and none of them wants a raw path. The **handoff** does still
 * need the whole key, and it never came through here: `flightHandoffEntry`
 * keeps its own fallback.
 */
export function flightFileLabel(flight: FlightFileFields, placeholder = '—'): string {
  if (flight.log_basename) return flight.log_basename;
  if (!flight.s3_key) return placeholder;
  // eslint-disable-next-line no-restricted-syntax -- legacy rows only: `log_basename` was backfilled only where the old index reached, so ~183 flights carry NULL and their key IS still a path. Behind the row's name, and a key with no last segment yields the placeholder rather than the raw path (#1294 review F4).
  return flight.s3_key.split('/').pop() || placeholder;
}

/**
 * Whether to offer Plot for a flight — two questions, both answered by the row.
 *
 * **Can it be named?** The viewer routes CSV-vs-BIN off the filename, so a log
 * we cannot name is one we cannot plot. That is {@link flightFileLabel} run
 * through the same extension rule the Files tab uses on a row's name — the
 * empty placeholder is deliberate, so "nothing to show" reads as "not
 * plottable" rather than as a dash with an extension test run on it.
 *
 * **Can it be addressed?** `object_id`, or a key to fall back on. Mirrors the
 * `flight.s3_key &&` half of the gate this replaces, and keeps every legacy row
 * that shows Plot today showing it: those rows all carry a path-shaped key.
 *
 * The gate this replaces was `isPlottableFile(flight.s3_key)` — an extension
 * check on the storage key, correct only while a key and a path spelled the
 * same thing. #39 Phase C ended that, and the button silently vanished from
 * every flight uploaded afterwards (#1294).
 *
 * Two residuals, both stated rather than papered over:
 *
 * - addressable but unnamed (minted key, no `log_basename`) stays **hidden** —
 *   we would have to hand the viewer a uuid as a filename and let it guess a
 *   pipeline;
 * - named but only key-addressable (minted key, no `object_id`) **shows**, and
 *   what crosses the boundary is `{s3_key: <uuid>, filename: <log_basename>}`.
 *   The name survives since #1297. It did not before: `analyticsHandoffPayload`
 *   re-derived the filename from any key it was given, and
 *   `<uuid>.split('/').pop()` is the uuid itself, so its `|| file.filename`
 *   fallback never fired and the good `log_basename` this module had just chosen
 *   was overwritten. Data Analysis Pro refused the entry either way (a uuid is
 *   not a display path and has no `.csv` suffix), so the outcome was an error
 *   rather than the wrong file — but it was refused as an unnameable key rather
 *   than reported as the named log it is, and the BIN/CSV router had a uuid to
 *   route on. Reversing that precedence was #1297; the composed test in
 *   `__tests__/flightFileRefs.test.ts` now pins the fixed behaviour, and pinned
 *   the broken one before that so the change could not happen silently.
 *
 *   `flights.object_id` is populated on every prod row, and Phase C's gate
 *   (#1278) refuses a CSV upload whose registry insert fails, so ingest cannot
 *   currently produce this shape. A button that errors beats one absent for
 *   reasons no user can see — the lesson of #1294 — but the error should be
 *   about the file the user named.
 */
export function isFlightPlottable(flight: FlightFileFields): boolean {
  if (!flight.object_id && !flight.s3_key) return false;
  return isPlottableFile(flightFileLabel(flight, ''));
}

/**
 * Strip the caller's own `organizations/<slug>/` prefix off a legacy key.
 *
 * Only meaningful for a path-shaped key: on a minted uuid this is a no-op, which
 * is precisely why the id has to lead in {@link flightHandoffEntry}.
 */
export function relativeLegacyKey(s3Key: string, orgSlug: string | null): string {
  if (!orgSlug) return s3Key;
  const prefix = `organizations/${orgSlug}/`;
  return s3Key.startsWith(prefix) ? s3Key.slice(prefix.length) : s3Key;
}

/**
 * The handoff payload entry for one flight — an ADDRESS, not a label.
 *
 * `object_id` leads because it is an address that cannot be *edited* into another
 * organization's (the bug #1231 closed on the agent side), because
 * `handoffRefsFromPayload` already prefers it, and because
 * `/api/s3/download-csv` resolves a **display path** and gates on a `.csv`
 * suffix — neither of which a minted key can satisfy. Without this, every
 * Phase-C flight answers "File must be a csv file".
 *
 * The `filename` is the row's name wherever there is one, and it is load-bearing
 * beyond display: the viewer routes CSV-vs-BIN off this string, so a uuid here
 * would push a `.BIN` log into the CSV pipeline.
 */
export function flightHandoffEntry(
  flight: FlightFileFields,
  orgSlug: string | null
): FlightHandoffEntry {
  const key = flight.s3_key ?? '';
  const relative = relativeLegacyKey(key, orgSlug);
  const filename = flight.log_basename || relative.split('/').pop() || relative;
  return flight.object_id
    ? { object_id: flight.object_id, filename }
    : { s3_key: relative, filename };
}
